/**
 * CJAS — documents attached to a service request.  Jira CA-27, CA-102.
 *
 * "A request submitted from the app lands in the inbox with its documents;
 * nikah needs document upload for prøvingsattest — encrypted storage, GDPR."
 *
 * Until 2026-09-10 the table `service_request_file` existed and nothing wrote
 * to it: a nikah could not carry its prøvingsattest at all. This module is the
 * RULES for an upload — which service takes documents and which ones, how big,
 * what kind of file, what a filename may say, how long it is kept. The
 * encryption itself is the API's (node:crypto, AES-256-GCM per file — see
 * repo/apps/api/src/files.js); core stays dependency-free.
 *
 * Which documents a service takes is read off `forms.js`, which renders the
 * form — the same rule as requests.KINDS: one list, derived, never retyped.
 *
 * A prøvingsattest carries the couple's fødselsnumre. The platform never
 * stores one as DATA (no field, no column, no log line); a scan of an official
 * document is different, and that is exactly why the file is encrypted at
 * rest, readable only by staff who may read the request, audited per
 * download, and deleted on a date set at upload.
 */

const forms = require('./forms');
const requests = require('./requests');

/** Per file, per request, and all together. A phone photo of an A4 page is 1–4 MB. */
const MAX_BYTES = 5 * 1024 * 1024;
const MAX_FILES = 4;
const MAX_TOTAL_BYTES = 10 * 1024 * 1024;
const MAX_FILENAME = 120;

/**
 * What is accepted, decided by the file's first bytes — never by the name or
 * by the type the client claims. A "PDF" that is an executable is refused.
 */
const TYPES = {
  'application/pdf': { ext: 'pdf', label_no: 'PDF' },
  'image/jpeg':      { ext: 'jpg', label_no: 'JPEG-bilde' },
  'image/png':       { ext: 'png', label_no: 'PNG-bilde' },
  'image/heic':      { ext: 'heic', label_no: 'HEIC-bilde (iPhone)' },
};

const startsWith = (b, sig, at = 0) => sig.every((x, i) => b[at + i] === x);

/** The real type of these bytes, or null. */
function sniff(bytes) {
  const b = bytes || [];
  if (b.length < 12) return null;
  if (startsWith(b, [0x25, 0x50, 0x44, 0x46, 0x2d])) return 'application/pdf';      // %PDF-
  if (startsWith(b, [0xff, 0xd8, 0xff])) return 'image/jpeg';
  if (startsWith(b, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) return 'image/png';
  if (startsWith(b, [0x66, 0x74, 0x79, 0x70], 4)) {                                   // ....ftyp
    const brand = String.fromCharCode(b[8], b[9], b[10], b[11]);
    if (['heic', 'heix', 'hevc', 'hevx', 'mif1', 'msf1'].includes(brand)) return 'image/heic';
  }
  return null;
}

/** The documents a service takes, from the form that renders them. */
function documentsFor(kind) {
  const s = forms.schema(requests.normaliseKind(kind));
  return s && Array.isArray(s.documents) ? s.documents : [];
}
const acceptsDocuments = (kind) => documentsFor(kind).length > 0;

/**
 * A filename as it may be stored and shown to staff: no path, no control
 * characters, no bidi overrides (a name that renders as "attest.pdf" and is
 * really "fdp.exe"), bounded length.
 */
const UNSAFE_RANGES = [[0x00, 0x1f], [0x7f, 0x9f], [0x200e, 0x200f], [0x202a, 0x202e], [0x2066, 0x2069]];
const unsafeChar = (ch) => { const c = ch.codePointAt(0); return UNSAFE_RANGES.some(([a, b]) => c >= a && c <= b); };
function safeFilename(name, fallback = 'dokument') {
  let s = String(name || '').split(/[\\/]/).pop();
  s = [...s].filter((ch) => !unsafeChar(ch)).join('').trim();
  if (s.length > MAX_FILENAME) {
    const dot = s.lastIndexOf('.');
    const ext = dot > 0 && s.length - dot <= 8 ? s.slice(dot) : '';
    s = s.slice(0, MAX_FILENAME - ext.length) + ext;
  }
  return s || fallback;
}

/**
 * Check a set of files for one request.
 *
 * `files`: [{ docType, filename, mime, bytes }] where `bytes` is a Uint8Array
 * (or Buffer) of the PLAINTEXT. Returns the files as they may be stored —
 * sniffed type, safe name — or the errors, per file, in Norwegian.
 */
function validate(kind, files) {
  const errors = [];
  const list = Array.isArray(files) ? files : [];
  if (!list.length) return { ok: true, errors, files: [] };
  if (!acceptsDocuments(kind)) {
    return { ok: false, files: [], errors: [{ index: null, field: 'files', reason: 'not_accepted',
      message_no: 'Denne tjenesten tar ikke imot vedlegg.' }] };
  }
  if (list.length > MAX_FILES) {
    return { ok: false, files: [], errors: [{ index: null, field: 'files', reason: 'too_many',
      message_no: `Maks ${MAX_FILES} vedlegg per henvendelse.` }] };
  }
  const allowed = documentsFor(kind).map(d => d.id);
  const out = [];
  let total = 0;
  list.forEach((f, index) => {
    const bytes = f && f.bytes;
    const size = bytes ? bytes.length : 0;
    total += size;
    const docType = String((f && f.docType) || '');
    if (!allowed.includes(docType)) {
      errors.push({ index, field: 'docType', reason: 'unknown_document',
        message_no: `Ukjent dokumenttype. Gyldige: ${allowed.join(', ')}.` });
    }
    if (!size) { errors.push({ index, field: 'bytes', reason: 'empty', message_no: 'Filen er tom.' }); return; }
    if (size > MAX_BYTES) {
      errors.push({ index, field: 'bytes', reason: 'too_large',
        message_no: `Filen er for stor (maks ${MAX_BYTES / 1024 / 1024} MB).` });
    }
    const real = sniff(bytes);
    const claimed = String((f && f.mime) || '').toLowerCase();
    const sameType = !claimed || claimed === real
      || (real === 'image/heic' && /^image\/hei[cf]$/.test(claimed))
      || (real === 'image/jpeg' && claimed === 'image/jpg');
    if (!real) {
      errors.push({ index, field: 'mime', reason: 'unsupported_type',
        message_no: 'Filtypen støttes ikke. Bruk PDF, JPEG, PNG eller HEIC.' });
    } else if (!sameType) {
      errors.push({ index, field: 'mime', reason: 'type_mismatch',
        message_no: 'Filen er ikke det den sier at den er.' });
    }
    const filename = safeFilename(f && f.filename, `${docType || 'dokument'}.${real ? TYPES[real].ext : 'bin'}`);
    // The NAME is stored in the clear so staff can see what they are
    // opening — so it must not carry a fødselsnummer ("attest_01019912345.pdf").
    if (forms.containsFodselsnummer(filename) || forms.isForbiddenField(filename)) {
      errors.push({ index, field: 'filename', reason: 'fodselsnummer_in_name',
        message_no: 'Filnavnet ser ut til å inneholde et fødselsnummer. Gi filen et annet navn.' });
    }
    out.push({ index, docType, filename, mime: real, size });
  });
  if (total > MAX_TOTAL_BYTES) {
    errors.push({ index: null, field: 'files', reason: 'too_large_total',
      message_no: `Vedleggene er til sammen for store (maks ${MAX_TOTAL_BYTES / 1024 / 1024} MB).` });
  }
  return { ok: errors.length === 0, errors, files: errors.length ? [] : out };
}

/** Required documents not yet attached — what the app should still ask for. */
function missing(kind, attachedDocTypes = []) {
  return documentsFor(kind).filter(d => d.required && !attachedDocTypes.includes(d.id))
    .map(d => ({ id: d.id, label_no: d.label_no }));
}

/**
 * When a document is deleted.
 *
 * The privacy policy draft (CA-102) says marriage documents are kept "6
 * months after the marriage". So: six months after the requested nikah date
 * when there is one and it is later than the upload, else six months after
 * the upload (requests.retentionFor). Other services: its default.
 * Returned as a local calendar date, YYYY-MM-DD.
 */
function retentionUntil(kind, uploadedAt = new Date(), requestedDate = null) {
  let anchor = new Date(uploadedAt);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(requestedDate || ''));
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12);
    if (d > anchor) anchor = d;
  }
  const until = requests.retentionFor(requests.normaliseKind(kind), anchor);
  const p = (n) => String(n).padStart(2, '0');
  return `${until.getFullYear()}-${p(until.getMonth() + 1)}-${p(until.getDate())}`;
}

module.exports = {
  MAX_BYTES, MAX_FILES, MAX_TOTAL_BYTES, MAX_FILENAME, TYPES,
  sniff, documentsFor, acceptsDocuments, safeFilename, validate, missing, retentionUntil,
};
